import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d918rqk6ci2plld1okhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  const footerLinks = [
    {
      title: 'Footwear',
      links: [
        { label: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
        { label: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
        { label: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
        { label: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
      ],
    },
    {
      title: 'Apparel',
      links: [
        { label: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
        { label: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
        { label: 'Pants/Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
        { label: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
      ],
    },
    {
      title: 'More Categories',
      links: [
        { label: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
        { label: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
        { label: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
        { label: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
      ],
    },
  ];

  return (
    <footer className="bg-foreground-900 text-background-50">
      <div className="max-w-[1200px] mx-auto px-4 md:px-10 py-16 md:py-20">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-14">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl md:text-2xl font-bold text-white tracking-tight">
              pandabuy<span className="text-primary-400">.skin</span>
            </span>
          </Link>
          <p className="text-foreground-400 text-sm max-w-[340px]">
            Your ultimate destination for curated PandabuySpreadsheet collections — discover fashion finds, compare prices, and shop smarter.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-14">
          {footerLinks.map((col) => (
            <div key={col.title}>
              <h4 className="text-sm font-semibold text-white mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <p className="font-heading text-xl text-white mb-2">&copy; 2026 pandabuy.skin</p>
            <p className="text-sm text-foreground-400 mb-5">All rights reserved</p>
            <Link to="/article/pandabuy-spreadsheet-guide" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors block mb-2">
              Shopping Guide
            </Link>
            <a href="#categories" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors block mb-2">
              Browse Categories
            </a>
            <Link to="/" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors block">
              Home
            </Link>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 pt-10 border-t border-foreground-700/40">
          <div className="lg:w-[30%]">
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Minimal%20creative%20workspace%20with%20fashion%20magazines%20and%20fabric%20samples%20on%20light%20oak%20desk%20warm%20natural%20sunlight%20from%20side%20window%20clean%20aesthetic%20with%20terracotta%20and%20sage%20green%20accents%20modern%20studio%20interior%20photography&width=800&height=600&seq=footer-2701-x4&orientation=landscape"
                alt="PandabuySpreadsheet curated fashion organization"
                title="PandabuySpreadsheet Curated Collection"
                className="w-full h-[200px] object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:w-[35%] flex items-center">
            <p className="font-heading text-2xl md:text-3xl text-foreground-300 leading-relaxed">
              Curated Finds.<br />Trusted Sources.<br />Global Shipping.
            </p>
          </div>

          <div className="lg:w-[35%]">
            <p className="text-sm text-foreground-400 mb-4">Get the latest PandabuySpreadsheet drops</p>
            <form
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="footer-subscription flex flex-col sm:flex-row gap-3"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="subscription-verification"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                className="flex-1 h-12 px-4 rounded-xl bg-foreground-800/60 border border-foreground-600/40 text-sm text-white placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="h-12 px-6 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {status === 'submitting' ? 'Sending...' : 'Subscribe'}
                <i className="ri-arrow-right-line ml-1.5"></i>
              </button>
            </form>
            {status === 'success' && (
              <p className="mt-3 text-xs text-accent-400">Thanks for subscribing! Check your inbox soon.</p>
            )}
            {status === 'error' && (
              <p className="mt-3 text-xs text-red-400">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}